import { RadioButton } from 'cpool-components/input/RadioButton';
import { MEASURING_UNITS, REAL_ESTATE_USAGES } from 'cpool-constants';
import type { ApplicantCreation } from 'cpool-schemas';
import type { Path } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuTrash2 } from 'react-icons/lu';

import { RealEstateLoans } from './RealEstateLoans';
import { FormCurrencyInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormCurrencyInput';
import { FormDropdown } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDropdown';
import { FormNumberInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormNumberInput';

export type RealEstateItemProps = {
    index: number;
    control: ReturnType<typeof useFormContext<ApplicantCreation>>['control'];
    onRemove: () => void;
    realEstateTypeOptions: { label: string; value: string }[];
};

export const RealEstateItem: React.FC<RealEstateItemProps> = ({ control, index, onRemove, realEstateTypeOptions }) => {
    const { t } = useTranslation();
    const { watch } = useFormContext<ApplicantCreation>();

    const nutzungsart = watch(`immobilien.${index}.nutzungsart`);
    const showRentedLivingSpace = nutzungsart === REAL_ESTATE_USAGES.EIGENGENUTZT_UND_VERMIETET;

    return (
        <div className="space-y-3 rounded bg-white p-2.5">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-midnight">
                    {t('REAL_ESTATE.LABELS.HEADER', { count: index + 1 })}
                </h4>
                <button
                    type="button"
                    onClick={onRemove}
                    className="p-2 text-alert-error hover:bg-alert-error/10 rounded"
                >
                    <LuTrash2 />
                </button>
            </div>

            {/* Nutzungsart - Radio buttons */}
            <div className="flex gap-2.5 items-center">
                <span className="w-[175px] shrink-0 text-sm text-midnight text-left">
                    {t('REAL_ESTATE.LABELS.USAGE_TYPE')}
                </span>
                <Controller
                    name={`immobilien.${index}.nutzungsart`}
                    control={control}
                    render={({ field }) => (
                        <div className="flex gap-4 w-full justify-between">
                            {Object.values(REAL_ESTATE_USAGES).map((usage) => (
                                <RadioButton
                                    key={usage}
                                    showBorder
                                    wrapperClassName="flex w-full justify-center"
                                    id={`immobilien.${index}.nutzungsart.${usage}`}
                                    name={`immobilien.${index}.nutzungsart`}
                                    value={usage}
                                    checked={field.value === usage}
                                    onChange={() => field.onChange(usage)}
                                    label={t(`REAL_ESTATE.USAGE.${usage}`)}
                                />
                            ))}
                        </div>
                    )}
                />
            </div>

            {/* Immobilienart - Dropdown */}
            <FormDropdown
                id={`immobilien.${index}.immobilienart` as Path<ApplicantCreation>}
                label={t('REAL_ESTATE.LABELS.TYPE')}
                options={realEstateTypeOptions}
                control={control}
            />

            <FormCurrencyInput
                id={`immobilien.${index}.wert` as Path<ApplicantCreation>}
                control={control}
                label={t('REAL_ESTATE.LABELS.VALUE')}
                min={0}
            />

            <FormCurrencyInput
                id={`immobilien.${index}.nebenkostenMonatlich` as Path<ApplicantCreation>}
                control={control}
                label={t('REAL_ESTATE.LABELS.ADDITIONAL_COSTS')}
                min={0}
            />

            <FormNumberInput
                ariaLabel={`${t('REAL_ESTATE.LABELS.LIVING_SPACE')} (${MEASURING_UNITS.SQUARE_METERS})`}
                id={`immobilien.${index}.wohnflaeche` as Path<ApplicantCreation>}
                control={control}
                label={t('REAL_ESTATE.LABELS.LIVING_SPACE')}
                measuringUnit={MEASURING_UNITS.SQUARE_METERS}
                min={0}
                allowDecimals={false}
            />

            {showRentedLivingSpace && (
                <FormNumberInput
                    ariaLabel={`${t('REAL_ESTATE.LABELS.RENTED_LIVING_SPACE')} (${MEASURING_UNITS.SQUARE_METERS})`}
                    id={`immobilien.${index}.vermieteteWohnflaeche` as Path<ApplicantCreation>}
                    control={control}
                    label={t('REAL_ESTATE.LABELS.RENTED_LIVING_SPACE')}
                    measuringUnit={MEASURING_UNITS.SQUARE_METERS}
                    min={0}
                    allowDecimals={false}
                />
            )}

            <FormCurrencyInput
                id={`immobilien.${index}.mieteinnahmenKaltMonatlich` as Path<ApplicantCreation>}
                control={control}
                label={t('REAL_ESTATE.LABELS.RENTAL_INCOME_COLD')}
                min={0}
            />

            <FormCurrencyInput
                id={`immobilien.${index}.mieteinnahmenWarmMonatlich` as Path<ApplicantCreation>}
                control={control}
                label={t('REAL_ESTATE.LABELS.TOTAL_REVENUE')}
                min={0}
            />

            <RealEstateLoans realEstateIndex={index} control={control} />
        </div>
    );
};
